import React from 'react';
import { TI_SENTIMENT } from '../../triage/config';
import type { TriageItem, TriageStatus } from '../../triage/types';
import { Icon } from '../Icon/Icon';
import { TriageDetailHeader } from './TriageDetailHeader';
import { TriageInsuranceCard } from './TriageInsuranceCard';
import { TriageNotesCard } from './TriageNotesCard';
import { TriagePatientInfoStrip } from './TriagePatientInfoStrip';
import { TriageSourceLink } from './TriageSourceLink';
import { TriageSuggestedActions } from './TriageSuggestedActions';
import { TriageTriggerBanner } from './TriageTriggerBanner';

export interface TriageAttentionDetailProps {
  item: TriageItem;
  noteText: string;
  setNoteText: (value: string) => void;
  onChangeStatus: (id: number, status: TriageStatus) => void;
  onChangeAssignee: (id: number, assigneeId: string | null) => void;
  onAddNote: (id: number) => void;
  onExecuteAction?: (itemId: number, actionId: string, editedBody?: string) => void;
  onOpenSource?: (item: TriageItem) => void;
}

export function TriageAttentionDetail({
  item,
  noteText,
  setNoteText,
  onChangeStatus,
  onChangeAssignee,
  onAddNote,
  onExecuteAction,
  onOpenSource,
}: TriageAttentionDetailProps) {
  const senti = TI_SENTIMENT[item.sentiment ?? 'Neutral'] ?? TI_SENTIMENT.Neutral!;

  return (
    <div
      className="cal-scroll flex min-h-0 flex-1 overflow-y-auto"
      style={{ background: 'var(--cream-0)' }}
    >
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 32px 60px', width: '100%' }}>
        <TriageDetailHeader
          item={item}
          onChangeStatus={onChangeStatus}
          onChangeAssignee={onChangeAssignee}
        />
        <TriageTriggerBanner
          triggeredBy={item.triggeredBy}
          sourceLabel={item.sourceLabel}
          surfacesIn={item.surfacesIn}
        />
        {item.patientInfo ? <TriagePatientInfoStrip info={item.patientInfo} /> : null}
        <TriageInsuranceCard
          item={item}
          {...(onOpenSource ? { onViewImages: () => onOpenSource(item) } : {})}
        />

        {item.aiSummary ? (
          <section className="v6-card v6-fade-up v6-d-2" style={{ marginBottom: 14 }}>
            <header
              style={{
                padding: '14px 20px 12px',
                borderBottom: '1px solid var(--cream-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon kind="sparkle" size={14} stroke={2} color="var(--ai-3)" />
              <span
                className="v6-display"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--ink-0)',
                  letterSpacing: '-0.01em',
                }}
              >
                Elva&apos;s summary
              </span>
              <span
                className="v6-mono"
                style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}
              >
                auto-generated
              </span>
            </header>
            <div style={{ padding: '16px 20px 18px' }}>
              <p
                style={{
                  fontSize: 13.5,
                  color: 'var(--ink-1)',
                  margin: 0,
                  letterSpacing: '-0.005em',
                  lineHeight: 1.65,
                }}
              >
                {item.aiSummary}
              </p>
              <div style={{ marginTop: 14 }}>
                <TriageSourceLink item={item} {...(onOpenSource ? { onOpen: onOpenSource } : {})} />
              </div>
            </div>
          </section>
        ) : null}

        {item.onlineBooking ? (
          <section className="v6-card v6-fade-up v6-d-3" style={{ marginBottom: 14 }}>
            <header
              style={{
                padding: '14px 20px 12px',
                borderBottom: '1px solid var(--cream-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon kind="globe" size={14} stroke={1.9} color="var(--ink-2)" />
              <span className="v6-display" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}>
                The request
              </span>
            </header>
            <div style={{ padding: '14px 20px 16px' }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-1)', lineHeight: 1.6 }}>{item.onlineBooking.request}</p>
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="v6-mono" style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>
                  Call: {item.onlineBooking.call}
                </span>
                {item.onlineBooking.hasRecording ? (
                  <>
                    <button type="button" className="ti-tag" style={{ fontSize: 11, padding: '3px 9px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Icon kind="play" size={11} stroke={2} /> Recording
                    </button>
                    <button type="button" className="ti-tag" style={{ fontSize: 11, padding: '3px 9px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Icon kind="doc" size={11} stroke={1.9} /> Transcript
                    </button>
                  </>
                ) : (
                  <span className="v6-mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>No recording — the call wasn&rsquo;t answered.</span>
                )}
              </div>

              <div style={{ marginTop: 16 }}>
                <div className="v6-eyebrow" style={{ fontSize: 10, marginBottom: 8 }}>ELVA&rsquo;s six checks</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {item.onlineBooking.checks.map((c) => (
                    <div
                      key={c.label}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '8px 12px',
                        borderRadius: 10,
                        background: 'var(--cream-1)',
                      }}
                    >
                      <span style={{ marginTop: 1, color: c.pass ? 'var(--success)' : 'var(--set-warning, #b5803d)', flex: 'none' }}>
                        <Icon kind={c.pass ? 'check-circ' : 'alert'} size={15} stroke={2} />
                      </span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-1)' }}>{c.label}</span>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{c.quote}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <TriageNotesCard
          item={item}
          noteText={noteText}
          setNoteText={setNoteText}
          onAdd={() => onAddNote(item.id)}
        />

        <section
          className="v6-card v6-fade-up v6-d-4"
          style={{
            marginBottom: 14,
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
            <span className="v6-eyebrow" style={{ fontSize: 10 }}>
              Tags
            </span>
            {item.tags.map((tag) => (
              <span key={tag} className="ti-tag" style={{ fontSize: 11, padding: '3px 9px' }}>
                {tag}
              </span>
            ))}
          </div>
          {item.sentiment ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="v6-eyebrow" style={{ fontSize: 10 }}>
                Sentiment
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: senti.color,
                  letterSpacing: '-0.005em',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: senti.dot,
                    flex: 'none',
                  }}
                />
                {item.sentiment}
              </span>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
